import { createAsyncThunk, ThunkAction, UnknownAction } from "@reduxjs/toolkit";
import { rdsMeeting_Dto, rdsMeeting_ResponseDto, rdsMeetingAll_RequestDto, rdsMeetingAll_ResponseDto } from "../../interfaces/dtos/rdsMeeting";
import dayjs from "dayjs";
import { rdsMeetingGet } from "../../utils/rds";
import { apiUrls } from "../../components/shared/apiUrls";
import { jsonToQueryString } from "../../utils/http";
import { RootState } from "../store";
import { loadRdsEvents } from "./rdsActionsEvent";
import { dateInReloadRange, getDateKey } from "../common/commonActions";
import { RdsState } from "./rdsSlice";
import { loadRdsEventStatusesFromMeeting } from "./rdsActionsEventStatus";
import { toUKDate } from "../../utils/commonUtils";

interface LoadRdsMeetingsArgs {
    date: Date | string;
    categoryCode: string;
    meetingIdsToLoadEvents?: number[];
}

export const loadRdsMeetings = createAsyncThunk(
    'rds/loadRdsMeetings',
    async ({ date, categoryCode, meetingIdsToLoadEvents }: LoadRdsMeetingsArgs, { getState, dispatch }) => {
        const request: rdsMeetingAll_RequestDto = {
            date: dayjs(date).format("YYYY-MM-DD"),
            offset: "+01:00",
            category: categoryCode
        };

        const response = await rdsMeetingGet<rdsMeetingAll_ResponseDto>(
            `${apiUrls.rdsMeeting.getAll}?${jsonToQueryString(request)}`
        );

        const responseData = response.data ?? [];
        if (meetingIdsToLoadEvents) {
            responseData.filter(r => meetingIdsToLoadEvents.some(m => m === r.id))
                .forEach(async (m) => {
                    // Don't add await so they can be loaded in the background
                    await dispatch(loadRdsEvents(m.id));
                    dispatch(loadRdsEventStatusesFromMeeting(m.id) as ThunkAction<unknown, unknown, unknown, UnknownAction>);
                })
        }

        const { rds } = getState() as RootState;
        const responseMeetingIds = responseData.map((m) => m.id);
        const otherMeetings = rds.rdsMeetings.filter((m) => !responseMeetingIds.some((id) => id === m.id));

        return [
            ...otherMeetings,
            ...responseData.map((m) => mapMeeting(m))
        ]
    }
);

export const loadRdsMeeting = createAsyncThunk(
    'rds/loadRdsMeeting',
    async (meetingId: number, { getState }) => {

        const response = await rdsMeetingGet<rdsMeeting_ResponseDto>(
            apiUrls.rdsMeeting.get(meetingId)
        );

        const { rds } = getState() as RootState;
        const otherMeetings = rds.rdsMeetings.filter((m) => m.id !== meetingId);

        return [...otherMeetings, mapMeeting(response.data)];
    }
);

const mapMeeting = (meeting: rdsMeeting_Dto) => {
    const startDate = toUKDate(meeting.startDate)!;
    return {
        ...meeting,
        startDate,
        dateKey: getDateKey(startDate!)
    }
}

// Check if we should reload or not - if in the past 2 days or prior data won't change,
// So if we already have the data we don't have to reload
export const mustReloadRdsMeetings = (date: Date, categoryCode: string, rdsState: RdsState) => {
    if (dateInReloadRange(date)) {
        return true;
    }

    // If we already have the items (and past items don't change - don't fetch)
    // Check for more than 1 item, as loading the event will load 1 meeting, but not all of them.
    const meetings = rdsState.rdsMeetings.filter((m) =>
        m.dateKey === getDateKey(date) && m.categoryCode === categoryCode
    )
    return meetings.length <= 1;
}
